import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (status === 429) {
        setErr(detail || "Too many reset requests. Please wait a few minutes and try again.");
      } else if (status === 502 || status === 503 || status === 504 || !ex?.response) {
        setErr("We couldn't reach the server just now. Please try again in a moment.");
      } else {
        setErr(formatApiError(detail) || "We couldn't send the reset link. Please try again.");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="forgot-password-page">
      <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-brand-muted mb-6 hover:text-brand-ink">
        <ArrowLeft size={14} /> Back to sign in
      </Link>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-7 shadow-soft">
        {!done ? (
          <>
            <div className="w-11 h-11 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-4">
              <Mail size={20} />
            </div>
            <h1 className="text-2xl font-bold text-brand-ink">Reset your password</h1>
            <p className="text-sm text-brand-muted mt-2 mb-5">Enter your email and we'll send a secure reset link. The link expires in 1 hour.</p>
            <form onSubmit={submit} className="space-y-4">
              <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address" data-testid="forgot-pw-email"
                className="w-full h-12 px-4 rounded-2xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-teal/30 text-sm" />
              {err && <p className="text-sm text-red-600" data-testid="forgot-pw-error">{err}</p>}
              <button type="submit" disabled={busy} data-testid="forgot-pw-submit"
                className="w-full h-12 rounded-2xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy/90 disabled:opacity-60 transition-all">
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-pw-done">
            <div className="w-11 h-11 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-4">
              <CheckCircle2 size={20} />
            </div>
            <h1 className="text-2xl font-bold text-brand-ink">Check your inbox</h1>
            <p className="text-sm text-brand-muted mt-2">If an account exists for <strong>{email}</strong>, a reset link is on the way. Check your spam folder if it doesn't arrive within a few minutes.</p>
            <Link to="/login" className="mt-6 inline-flex items-center justify-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 transition-all">Back to sign in</Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
